'use strict';

import {objectAssign} from '../helpers/utilities';
import {callbackOnElements} from '@borngroup/born-utilities';
import {getSalutationData} from '../helpers/addressUtilities';

/**
 * Copies the existing "address summary template" from the DOM and returns it.
 * A `context` argument may be passed to provide more customizations.
 */
function _getSummaryTemplate(context, attributeUpdate, show) {
    let summaryTemplate = document.querySelector('[data-address-summary="address-summary-template"]').cloneNode(true);

    context ? summaryTemplate.classList.add(`address-summary--${context}`) : '';

    attributeUpdate ? summaryTemplate.setAttribute('data-address-summary', attributeUpdate) : '';

    show ? summaryTemplate.classList.remove('hidden') : '';

    return summaryTemplate;
}

/**
 * Updates the `addressData` with the custom first/last name values if they're enabled and available.
 * @param {[type]} addressData [description]
 */
function _setCustomNames(addressData) {
    if (addressData.useCustomNames) {
        addressData.firstName = addressData.customFirstName || addressData.firstName;
        addressData.lastName = addressData.customLastName || addressData.lastName;
    }
}

function _setLocalizedStoreAddress(addressData) {
    if (addressData.localizedStoreAddress) {
        addressData.firstName = addressData.localizedStoreAddress.name || addressData.firstName;
        addressData.address1 = addressData.localizedStoreAddress.address1 || addressData.address1;
        addressData.address2 = addressData.localizedStoreAddress.address2 || addressData.address2;
        addressData.city = addressData.localizedStoreAddress.city || addressData.city;
    }
}

/**
 * Populate the Billing Address Summary View
 * @param {string} addressContainer - the top level DOM element for a unique address summary
 * @param {Object} address - the address data
 */
export function populateAddressSummary(addressContainer, addressData) {
    addressContainer = typeof addressContainer === 'string' ? document.querySelector(addressContainer) : addressContainer;

    if (addressContainer) {
        _setCustomNames(addressData);
        _setLocalizedStoreAddress(addressData);

        for (let key in addressData) {
            let matchedSummaryEl = addressContainer.querySelector(`[data-address-summary="${key}"]`);

            if (matchedSummaryEl) {
                let hasDisplayValue = addressData[key] && addressData[key].hasOwnProperty('displayValue'),
                    displayValue = addressData[key] ? (hasDisplayValue ? addressData[key].displayValue : addressData[key]) : null;

                //Only display address data properties that are string or numbers.
                //Only use boolean values to toggle visibility.
                if (typeof displayValue === 'string' || typeof displayValue === 'number') {
                    matchedSummaryEl.textContent = displayValue;
                }

                //Make sure address properties and sections that have values are shown.
                if (displayValue) {
                    matchedSummaryEl.classList.remove('hidden');
                } else {
                    //Hide properties and sections who match and have falsey values.
                    //Example sections: gift message, shipping methods.
                    matchedSummaryEl.classList.add('hidden');
                }
            }
        }

        // Insert delivery ETA
        let deliveryEta = addressContainer.querySelector('[data-checkout-component="shipping-method-eta"]'),
            deliveryEtaDate = addressContainer.querySelector('[data-checkout-component="shipping-method-eta-date"]'),
            deliveryLocation = addressContainer.querySelector('[data-checkout-component="shipping-method-location"]');

        if (deliveryEta && deliveryEtaDate && addressData.shippingMethodETA) {
            if (addressData && addressData.countryCode && addressData.countryCode.value == 'KR') {
                let dateMillSec = new Date(new Date(addressData.shippingMethodETA).getTime()).setHours(0, 0, 0, 0);
                deliveryEtaDate.textContent = new Date(dateMillSec).getFullYear() + '/' + ('0' + (new Date(dateMillSec).getMonth() + 1)).slice(-2) + '/' + ('0' + (new Date(dateMillSec).getDate())).slice(-2);
            } else {
                deliveryEtaDate.textContent = addressData.shippingMethodETA;
            }
            deliveryEta.classList.remove('hidden');
        }

        if (deliveryLocation && addressData.shippingLocation && addressData.shippingLocation == 'CHD6') {
            deliveryLocation.classList.remove('hidden');
        }
    }
}

/**
 * updates the totals summary
 * @param {Array} totals - the totals data
 */
export function updateTotals(totals, shipping) {
    for (let key in totals) {
        callbackOnElements(`[data-totals-component="${key}"]`, function(matchedTotalEl) {
            let matchedValueEl = matchedTotalEl.querySelector('[data-totals-component="value"]');
            if (key === 'totalShippingCost') {
                let matchedLabelEl = matchedTotalEl.querySelector('[data-totals-component="label"]');
                matchedLabelEl.textContent = shipping[0].selectedShippingMethod.displayName;
            }

            if (matchedValueEl && typeof totals[key] === 'string') {
                matchedValueEl.textContent = totals[key];
            } else if (matchedValueEl && totals[key] && typeof totals[key] === 'object') {
                if (totals[key].value) {
                    matchedValueEl.textContent = totals[key].formatted;

                    matchedTotalEl.classList.remove('hidden');
                } else {
                    matchedTotalEl.classList.add('hidden');
                }
            }
        });
    }
}

/**
 * Update the read-only portion of the shipment display (per PLI)
 * @param {Object} productLineItem - the productLineItem model
 * @param {Object} shipping - the shipping (shipment model) model
 * @param {Object} order - the order model
 * @param {Object} [options] - options for updating PLI summary info
 * @param {Object} [options.keepOpen] - if true, prevent changing PLI view mode to 'view'
 */
export function updateShippingSummaryPLI(productLineItem, shipping, order, options) {
    let pliUUIDEl = document.querySelector(`input[value="${productLineItem.UUID}"]`),
        form = pliUUIDEl ? pliUUIDEl.form : false;

    if (form && order.usingMultiShipping && shipping.shippingAddress) {
        let summaryContainer = form.querySelector('[data-address-summary*="main"]');

        updateShippingSummaryItem(shipping, summaryContainer);

        summaryContainer.classList.remove('hidden');
        summaryContainer.classList.add('address-summary--multi-ship-summary');
        summaryContainer.setAttribute('data-address-summary', `main-${shipping.UUID}`);
    }

    $('body').trigger('shipping:updateShippingSummaryPLI', {
        productLineItem: productLineItem,
        shipping: shipping,
        order: order,
        options: options
    });
}

/**
 * Updates the order product shipping summary for an order model
 * @param {Object} order - the order model
 */
export function updateMultiShippingOrderSummary(order) {
    let productList = document.querySelector('[data-checkout-component="product-list"]');

    if (order.usingMultiShipping) {
        let summaryListFragment = document.createDocumentFragment();

        order.shipping.forEach(function(shipping) {
            let shippingSummary = _getSummaryTemplate('line-item', `main-${shipping.UUID}`, true);

            //Move the existing items in the DOM to their corresponding position in the current shipment.
            shipping.productLineItems.items.forEach(function(productLineItem) {
                let itemSummary = productList.querySelector(`[data-cart-line-item="${productLineItem.UUID}"]`);

                summaryListFragment.appendChild(itemSummary);
            });

            updateShippingSummaryItem(shipping, shippingSummary);

            summaryListFragment.appendChild(shippingSummary);
        });

        //Clear the product summary container before any further DOM insertions.
        productList.textContent = '';
        productList.appendChild(summaryListFragment);
    } else {
        let shippingSummaryList = productList.querySelectorAll('[data-address-summary*="main"]');

        [].forEach.call(shippingSummaryList, currentSummary => currentSummary.parentNode.removeChild(currentSummary));
    }
}

/**
 * Updates the target `summaryContainer` HTML with the data provided in `shipping`.
 * @return {[type]}                  [description]
 */
export function updateShippingSummaryItem(shipping, summaryContainer) {
    let shipMethod = shipping.selectedShippingMethod,
        emailData = {
            email: null
        },
        shipMethodData = {
            shippingMethod: !!shipMethod, //Force set to boolean, shipping method section is hidden by `populateAddressSummary` if false.
            shippingMethodDisplayName: shipMethod.displayName,
            shippingMethodArrivalTime: shipMethod.estimatedArrivalTime ? `( ${shipMethod.estimatedArrivalTime} )` : '',
            shippingMethodCost: shipMethod.shippingCost,
            shippingMethodETA: (shipping.shippingAddress && (shipping.shippingAddress.countryCode.value === 'AE' || shipping.shippingAddress.countryCode.value === 'SA')) ? shipMethod.localizedEtaFluent ? shipMethod.localizedEtaFluent : '' : shipMethod.etaFluent ? shipMethod.etaFluent : '',
            shippingLocation: shipMethod.locationRef ? shipMethod.locationRef : ''
        },
        giftData = {
            gift: shipping.isGift,
            giftMessage: shipping.giftMessage
        },
        salutationData = getSalutationData(shipping.shippingAddress),
        addressData = objectAssign({}, shipping.shippingAddress, shipMethodData, giftData, emailData, salutationData);

    populateAddressSummary(summaryContainer, addressData);
}

/**
 * updates the order shipping summary for an order shipment model
 * @param {Object} shipping - the shipping (shipment model) model
 * @param {Object} order - the order model
 */
export function updateShippingSummaryList(shipping, order) {
    callbackOnElements(`[data-address-summary="main-${shipping.UUID}"]`, updateShippingSummaryItem.bind(this, shipping));

    $('body').trigger('shipping:updateShippingSummaryList', { shipping: shipping, order: order });
}
